import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getVehicles } from "@/api/vehicles";
import { vehicleLabel } from "@/features/routes/lib/vehicleLabel";
import type { PaginatedVehicles } from "@/types/vehicles";

const PAGE_SIZE = 20;
/** Debounce delay in ms for the search input. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The minimum a caller needs to know about a preselected vehicle in order to
 * label it without a request: exactly the shape `vehicleLabel` consumes.
 *
 * Deliberately structural rather than `Vehicle`: the nested `route.vehicle` on
 * `RouteDetail` is a 3-field summary, not a full `Vehicle`, and requiring the
 * full type would force the edit modal into a fetch it does not need.
 */
export interface InitialVehicle {
  id: number;
  name: string;
  registration_number?: string;
}

interface Props {
  /** Current selected vehicle id as string, "" means no vehicle selected. */
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  /**
   * The vehicle `value` refers to, when the CALLER already has it.
   *
   * TASK-4912. The vehicles query is gated on `enabled: open` so a page full of
   * rows does not fetch a list nobody opened. The cost of that gate was that a
   * PRESELECTED vehicle had no list to be found in, so the control rendered the
   * raw id — «#8» where «Renault Master · A 1234-7» belonged — until the
   * dispatcher opened the dropdown. Edit modals hold the whole route, including
   * `route.vehicle`, so passing it here labels the field on first render at a
   * cost of zero requests and keeps the lazy gate intact for callers that have
   * no preselection.
   *
   * Only used while the vehicle is absent from the fetched pages; once the list
   * loads, the fetched record wins, so a name edited elsewhere is never stale.
   */
  initialVehicle?: InitialVehicle | null;
  /**
   * Accessible name for the text input. REQUIRED in practice: this control had
   * NO accessible name at all (no aria-label, no associated <label>), which is
   * the BUG-504/508 family — a screen-reader user met an unnamed text box.
   * Optional only so existing call sites keep compiling; every call site in the
   * tree passes it.
   */
  "aria-label"?: string;
}

export function VehicleCombobox({
  value,
  onChange,
  disabled = false,
  initialVehicle = null,
  "aria-label": ariaLabel,
}: Props) {
  const { t } = useTranslation();
  const listId = useId();

  const [inputText, setInputText] = useState("");
  const [open, setOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce the search query to avoid hammering the API on every keystroke.
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((text: string) => {
    setInputText(text);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(text);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery<PaginatedVehicles>({
      queryKey: ["vehicles-combobox", debouncedSearch],
      queryFn: ({ pageParam }) =>
        getVehicles({
          page: typeof pageParam === "number" ? pageParam : 1,
          page_size: PAGE_SIZE,
          search: debouncedSearch || undefined,
          is_active: true,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const fetched = allPages.reduce((sum, p) => sum + p.results.length, 0);
        return fetched < lastPage.count ? allPages.length + 1 : undefined;
      },
      enabled: open,
    });

  // Flatten all pages into a single vehicle list.
  const allVehicles = data?.pages.flatMap((p) => p.results) ?? [];

  /**
   * Display label for the current selection, resolved in priority order:
   *
   *   1. the vehicle found in the fetched pages — freshest, wins once loaded;
   *   2. `initialVehicle` from the caller — labels a preselection on the FIRST
   *      render, before the gated list has fetched anything;
   *   3. a translated «Транспортное средство №8» for an id we cannot resolve.
   *
   * Step 3 is the deleted / cross-tenant / deactivated case. It deliberately
   * still renders SOMETHING: an empty control next to a set `value` reads as
   * "no vehicle selected", and saving from that state would silently clear a
   * real assignment. That is the exact failure the warehouse selects shipped
   * earlier this sprint. Naming the id keeps the field visibly non-empty and
   * tells the dispatcher the assignment is intact but unresolvable, instead of
   * the bare «#8» that read as a rendering bug.
   */
  const fetchedVehicle = allVehicles.find((v) => String(v.id) === value);
  const preselected =
    initialVehicle && String(initialVehicle.id) === value
      ? initialVehicle
      : null;
  const resolvedVehicle = fetchedVehicle ?? preselected;
  const selectedLabel = resolvedVehicle
    ? vehicleLabel(resolvedVehicle)
    : value
      ? t("routes.vehicleById", { id: value })
      : "";

  // Sync the input text when the selection changes externally (e.g. modal reset).
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (prevValueRef.current !== value && !open) {
      setInputText(selectedLabel);
    }
    prevValueRef.current = value;
  }, [value, selectedLabel, open]);

  // When the dropdown opens, show the currently selected vehicle name in the input.
  const handleFocus = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    // Show all vehicles (clear search) so user can scroll freely or type to narrow.
    setInputText("");
    setDebouncedSearch("");
  }, [disabled]);

  const handleBlur = useCallback(() => {
    // Delay so that onMouseDown on an option can fire before the list closes.
    setTimeout(() => {
      setOpen(false);
      // Restore the label of the selected vehicle in the input.
      setInputText(selectedLabel);
    }, 150);
  }, [selectedLabel]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      setInputText(selectedLabel);
    }
  }

  function handleSelect(vehicleId: string) {
    const vehicle = allVehicles.find((v) => String(v.id) === vehicleId);
    setInputText(vehicle ? vehicleLabel(vehicle) : "");
    setOpen(false);
    onChange(vehicleId);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    handleSearchChange(e.target.value);
    // If text is cleared, reset selection.
    if (!e.target.value) {
      onChange("");
    }
  }

  // Infinite scroll: when user scrolls near the bottom of the list, load the next page.
  const listRef = useRef<HTMLUListElement>(null);
  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const isDisabled = disabled || false;

  return (
    <div className="relative">
      <input
        type="text"
        value={
          open
            ? inputText
            : isLoading && !open
              ? t("routes.loading")
              : selectedLabel || inputText
        }
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        placeholder={t("routes.noVehicle")}
        // Combobox semantics + a real accessible name. Without these the input
        // announced as a bare text box with no name at all.
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open && !isDisabled}
        aria-controls={listId}
        aria-autocomplete="list"
        // A long "name · plate" scrolls horizontally inside the field natively;
        // `title` keeps the full value reachable on hover.
        title={selectedLabel || undefined}
        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary disabled:cursor-not-allowed disabled:opacity-60"
        autoComplete="off"
      />
      {open && !isDisabled && (
        <ul
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          ref={listRef}
          onScroll={handleListScroll}
          className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          {/* "No vehicle" option */}
          <li>
            <button
              type="button"
              onMouseDown={() => {
                setInputText("");
                setOpen(false);
                onChange("");
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-500 hover:bg-brand-primary/10"
            >
              {t("routes.noVehicle")}
            </button>
          </li>
          {isLoading && allVehicles.length === 0 && (
            <li className="px-4 py-2.5 text-sm text-gray-400">
              {t("routes.loading")}
            </li>
          )}
          {allVehicles.map((v) => {
            const label = vehicleLabel(v);
            return (
              <li key={v.id}>
                <button
                  type="button"
                  onMouseDown={() => handleSelect(String(v.id))}
                  title={label}
                  className="w-full truncate px-4 py-2.5 text-left text-sm text-gray-900 hover:bg-brand-primary/10"
                >
                  {label}
                </button>
              </li>
            );
          })}
          {!isLoading && allVehicles.length === 0 && (
            <li className="px-4 py-2.5 text-sm text-gray-500">
              {t("common.noResults", { defaultValue: "No results" })}
            </li>
          )}
          {isFetchingNextPage && (
            <li className="px-4 py-2.5 text-center text-sm text-gray-400">
              {t("routes.loading")}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
